/*
 * Links the embroidery fee line to an embroidered garment.
 *
 * The garment itself is added by the theme's product form (assets/product-form.js),
 * with the embroidery properties that pointelle-customizer.js puts on the form.
 * This file only adds the fee line under that exact garment line, so the same
 * size and color can be added again with a different icon as a separate pair.
 */
(function (scope) {
  const DEFAULT_FEE_ERROR = 'The embroidery could not be added, so your garment was not added either. Please try again.';
  const INTERRUPTED_ERROR = 'The connection was interrupted. Please review your cart before trying again.';

  /**
   * @typedef {Object} CartLine
   * @property {string} key - Cart line key
   * @property {number} variant_id - Variant ID
   * @property {number} quantity - Line quantity
   * @property {string} [product_title] - Product title
   * @property {Object<string, string>} [properties] - Line item properties
   * @property {{ parent_key: string }} [parent_relationship] - Parent line, for nested lines
   */

  /**
   * @param {typeof fetch} fetcher - Fetch implementation (window.fetch in the browser)
   * @param {string} [base] - Shopify routes root, e.g. '/' or '/fr/'
   */
  function createClient(fetcher, base = '/') {
    const request = async (path, body) => {
      const options = body
        ? { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) }
        : { headers: { Accept: 'application/json' }, cache: 'no-store' };
      const response = await fetcher(base + path, options);
      const data = await response.json();

      if (!response.ok || data.status >= 400 || data.errors) {
        throw new Error(data.description || data.message || 'Your cart could not be updated. Please try again.');
      }

      return data;
    };

    const getCart = () => request('cart.js');

    // sectionsUrl is a page path that already carries any locale prefix, so it is not joined to base.
    const getSections = async (sections, sectionsUrl) => {
      if (!sections.length) return undefined;

      const url = new URL(sectionsUrl || base, 'https://shop.invalid');
      url.searchParams.set('sections', sections.join(','));
      const response = await fetcher(url.pathname + url.search, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      return response.ok ? response.json() : undefined;
    };

    /**
     * Adds the fee line for a garment line that was just added to the cart.
     * If the fee cannot be added, removes that garment line again so nothing
     * embroidered is left in the cart without its fee.
     *
     * @param {CartLine} garment - The line returned by /cart/add.js for the garment
     * @param {Object} [options]
     * @param {string[]} [options.sections] - Section IDs to render in the response
     * @param {string} [options.sectionsUrl] - Page path the sections render for
     * @param {string} [options.errorMessage] - Shopper-facing message when the fee fails
     * @returns {Promise<Object>} The /cart/add.js response for the fee (with sections)
     */
    async function addFee(garment, { sections = [], sectionsUrl, errorMessage = DEFAULT_FEE_ERROR } = {}) {
      const properties = garment?.properties || {};
      const pair = properties._pointelle_pair;
      const feeVariantId = Number(properties._pointelle_fee_variant);
      const quantity = garment?.quantity;

      if (!garment?.key || !pair || !feeVariantId || !Number.isInteger(quantity) || quantity < 1) {
        throw new Error(errorMessage);
      }

      const fee = {
        id: feeVariantId,
        quantity,
        parent_line_key: garment.key,
        properties: {
          _pointelle_pair: pair,
          _pointelle_icon: properties._pointelle_icon,
          _pointelle_role: 'icon',
          Icon: properties.Icon,
          'For garment': garment.product_title,
          _pointelle_parent_variant: String(garment.variant_id),
          _pointelle_image: properties._pointelle_image,
        },
      };

      try {
        return await request('cart/add.js', { items: [fee], sections, sections_url: sectionsUrl });
      } catch (error) {
        // A failed response can still have reached Shopify. Inspect this pair only;
        // never change another line in the cart.
        try {
          const cart = await getCart();
          const pairLines = cart.items.filter((item) => item.properties?._pointelle_pair === pair);
          const linkedFee = pairLines.find(
            (item) =>
              item.properties._pointelle_role === 'icon' &&
              item.parent_relationship?.parent_key === garment.key &&
              item.quantity === quantity
          );

          if (linkedFee) return { ...cart, sections: await getSections(sections, sectionsUrl) };

          if (pairLines.length) {
            await request('cart/update.js', { updates: Object.fromEntries(pairLines.map((item) => [item.key, 0])) });
          }
        } catch (recoveryError) {
          throw new Error(INTERRUPTED_ERROR);
        }

        throw new Error(errorMessage);
      }
    }

    return { addFee, getCart, request };
  }

  /**
   * Browser entry point used by assets/product-form.js.
   * @param {CartLine} garment - The garment line returned by /cart/add.js
   * @param {Object} [options] - See addFee
   */
  const addFee = (garment, options) =>
    createClient(window.fetch.bind(window), window.Shopify?.routes?.root || '/').addFee(garment, {
      errorMessage: scope.PointelleOrders?.messages?.feeError,
      ...options,
    });

  scope.PointelleOrders = { createClient, addFee, messages: scope.PointelleOrders?.messages || {} };
  if (typeof module !== 'undefined') module.exports = { createClient };
})(typeof window === 'undefined' ? globalThis : window);
